import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { useSettings } from '../hooks/useData';
import { resolveTheme } from '../services/settingsService';
import { AuthProvider } from '../auth/AuthContext';
import { AuthGate } from '../auth/AuthGate';
import { ActiveDatabaseProvider } from '../database/ActiveDatabaseContext';
import { SyncProvider } from '../sync/SyncContext';

interface ToastValue { message: string; kind: 'success' | 'error'; }
interface ToastContextValue { showToast: (message: string, kind?: ToastValue['kind']) => void; }
const ToastContext = createContext<ToastContextValue>({ showToast: () => undefined });

export const useToast = () => useContext(ToastContext);

const ApplicationProviders = ({ children }: PropsWithChildren) => {
  const settings = useSettings();
  const [toast, setToast] = useState<ToastValue>();
  const showToast = useCallback((message: string, kind: ToastValue['kind'] = 'success') => setToast({ message, kind }), []);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    const apply = () => {
      const effective = resolveTheme(settings.theme, matchMedia('(prefers-color-scheme: dark)').matches);
      root.dataset.theme = effective;
    };
    apply();
    if (settings.theme !== 'system') return;
    const media = matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(undefined), 3400);
    return () => clearTimeout(timer);
  }, [toast]);

  const value = useMemo(() => ({ showToast }), [showToast]);
  return <ToastContext.Provider value={value}>
    {children}
    {toast && <div className={`toast toast--${toast.kind}`} role="status">{toast.message}</div>}
  </ToastContext.Provider>;
};

export const Providers = ({ children }: PropsWithChildren) => <AuthProvider>
  <AuthGate>
    <ActiveDatabaseProvider>
      <SyncProvider><ApplicationProviders>{children}</ApplicationProviders></SyncProvider>
    </ActiveDatabaseProvider>
  </AuthGate>
</AuthProvider>;
